import { useMemo } from "react";

import { useQuery } from "@apollo/client";
import { ArpFields, ArpsDesignerKind } from "constants/forecasts.constants";

import useBetaFeatures from "hooks/useBetaFeatures";

import { TEMPLATES } from "api/userArps";

import {
  SegmentTemplate,
  TemplateType,
  TypeWellTemplate
} from "../models/SegmentTemplate";
import useSegmentTemplates from "./useSegmentTemplates";

export function useTypeWellTemplates(kind?: ArpsDesignerKind): SegmentTemplate[] {
  const segmentTemplates = useSegmentTemplates();
  const { hasFeature } = useBetaFeatures();
  const hasForecastSteamFeature = hasFeature("Forecast Steam");

  const { loading, data } = useQuery(TEMPLATES);

  return useMemo(() => {
    if (loading) return [];

    if (data) {
      return data.templates
        .filter((template: TypeWellTemplate) => {
          // Filter out SAGD template if "Forecast Steam" feature flag is not enabled
          if (template.type === TemplateType.SAGD && !hasForecastSteamFeature) {
            return false;
          }
          return true;
        })
        .map((template: TypeWellTemplate) => {
          const segment = segmentTemplates.find(
            (segment: SegmentTemplate) => segment.type === template.type
          );
          if (!segment) return null;

          const rowHeaders = ["Template", ...template.headers.map((h) => h.name)];
          const displayHeaders = [
            "Template",
            ...template.headers.map((h) => h.displayName)
          ];

          rowHeaders.splice(rowHeaders.length - 1, 0, "End Date");
          displayHeaders.splice(displayHeaders.length - 1, 0, "End Date");

          const qiIndex = rowHeaders.indexOf("Qi");
          if (qiIndex !== -1) {
            rowHeaders.splice(qiIndex + 1, 0, "maxRate");
            displayHeaders.splice(qiIndex + 1, 0, "Max Rate");
          }

          // Add "Restricted EUR" and "Cutoff Qf" (but not in Forecasting mode)
          if (kind !== ArpsDesignerKind.Forecasting) {
            const eurIndex = rowHeaders.indexOf(ArpFields.EUR);
            if (eurIndex !== -1 && !rowHeaders.includes(ArpFields.RestrictedEUR)) {
              // Insert both items in order to maintain sync between rowHeaders and displayHeaders
              rowHeaders.splice(
                eurIndex + 1,
                0,
                ArpFields.RestrictedEUR,
                ArpFields.QfAtCutoff
              );
              displayHeaders.splice(eurIndex + 1, 0, "EUR.Cutoff", "Q.f.Cutoff");
            }
          }

          return {
            ...segment,
            name: template.name,
            numberOfSegments: template.numberOfSegments,
            rowHeaders,
            displayHeaders,
            update: segment.update,
            initialData: segment.initialData,
            extendArps: segment.extendArps
          } as SegmentTemplate;
        })
        .filter((t) => t !== null) as SegmentTemplate[];
    }

    return [];
  }, [loading, data, segmentTemplates, kind, hasForecastSteamFeature]);
}
